import { Button } from "components/Button/Button";
import { SchemaFormFragment } from "components/Form/components/SchemaForm/fragments/SchemaFormFragment";
import { Icon } from "components/Icon/Icon";
import { Popover } from "components/Popover/Popover";
import { TableColumn } from "components/Table/tableUtils";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RouterContext } from "utils/RouterManager/RouterManager";

// Type-aware default widths for custom-object ATTRIBUTE columns in list views.
// Native table auto-layout sizes columns by the intrinsic content of the first
// rendered rows, so early/long columns hog the available width and later
// attribute columns collapse below readability (Margot, intake ad1eb647). Give
// each attribute column a width FLOOR (so it can't be squeezed to nothing) and a
// CAP (so one long value can't hog the row), chosen by the attribute's schema-
// form element type. The object_template CO list scrolls horizontally inside its
// own subrecord-table scroller, so these floors never push the page into
// overflow. Keyed by `schema.element`; the optional-* variants share a width.
const ATTRIBUTE_COLUMN_WIDTH: Record<string, { minWidth: number; maxWidth: number }> = {
    // Short, bounded value types
    "form-checkbox": { minWidth: 80, maxWidth: 120 },
    // Boolean tri-state toggle (false | toggle | true). The floor keeps the whole
    // control contained at the narrowest draggable width so it can't spill into
    // the neighbouring column (operator-task-fe-resizable-column-narrow-width-
    // behavior); this is the safe full-control floor — a narrower toggle-only
    // floor (dropping the text labels below it) is a follow-up refinement.
    "form-boolean": { minWidth: 140, maxWidth: 160 },
    "form-dropdown": { minWidth: 140, maxWidth: 240 },
    "form-number": { minWidth: 100, maxWidth: 160 },
    "form-integer": { minWidth: 100, maxWidth: 160 },
    "form-datetime": { minWidth: 160, maxWidth: 200 },
    "form-date": { minWidth: 130, maxWidth: 170 },
    "form-time": { minWidth: 110, maxWidth: 150 },
    "form-password": { minWidth: 120, maxWidth: 220 },
    "form-secret-text": { minWidth: 120, maxWidth: 220 },
    // Single-line free text
    "form-text": { minWidth: 160, maxWidth: 320 },
    // Large / flexible content (clipped/height-bounded in the cell renderer)
    "form-markdown": { minWidth: 260, maxWidth: 480 },
    "form-textarea": { minWidth: 240, maxWidth: 480 },
    "form-code": { minWidth: 260, maxWidth: 480 },
    "form-json": { minWidth: 240, maxWidth: 480 },
};

const DEFAULT_ATTRIBUTE_COLUMN_WIDTH = { minWidth: 150, maxWidth: 320 };

/**
 * Resolve a `cellStyle` (in the shape RecordTable threads onto both the body and
 * header cell — `{ style: { minWidth, maxWidth } }`) for an attribute column,
 * from its schema-form element. `form-optional-*` variants map to the same width
 * as their required counterpart.
 */
export const getAttributeColumnCellStyle = (schema: any) => {
    const element: string = schema?.element ?? "";
    const normalizedElement = element.replace("form-optional-", "form-");
    const width = ATTRIBUTE_COLUMN_WIDTH[normalizedElement] ?? DEFAULT_ATTRIBUTE_COLUMN_WIDTH;
    return { style: { minWidth: width.minWidth, maxWidth: width.maxWidth } };
};

// Custom-object attribute element types that render as a live control (a compact
// switch) and therefore commit immediately on a single discrete interaction — the
// "immediately editable" set for inline list-editing
// (operator-task-fe-inline-edit-list-views, save-model Option A). A boolean
// renders a compact switch that reads as a control at rest, so it stays live in
// the cell.
//
// Enum dropdowns render as a badge at rest and reveal the live dropdown on click
// (the click-to-reveal PICKER set below), so an enum cell shows a badge rather
// than a permanently-open control in every list row. Popover-overlay
// text/markdown/json (slice (c)), click-to-edit string/integer (slice (b)), and
// click-to-reveal date/time pickers are handled by their own sets; every other
// element type (and non-writable rows) stays read-only.
const IMMEDIATE_EDITABLE_ELEMENTS = new Set([
    "form-boolean",
    "form-optional-boolean",
]);

// Custom-object attribute element types edited via a CLICK-TO-REVEAL control: an
// enum dropdown, or a date / time / datetime picker. The read-only value (a badge
// for enum dropdowns, the formatted date/time otherwise) shows until the cell is
// clicked, then the live control is revealed and commits on its single discrete
// `onChange` (a confirmed pick), matching the read-only-then-click-to-edit shape
// of the scalar string/integer cells rather than showing a permanently-open input
// in every list row.
//
// These need their own handling (not the scalar commit-on-blur cell): the antd
// DatePicker/TimePicker calendar/time panel is portaled OUTSIDE the cell (a
// document-level `.ant-picker-dropdown`), so a click into the panel blurs the
// cell — a commit-on-blur model would close the editor before the pick lands. The
// enum dropdown's menu renders INLINE (Autocomplete `menuPortalTarget: null`), so
// it sits inside the cell and the same portal-aware listener contains it. The
// cell therefore commits on `onChange` and exits edit mode via a portal-aware
// outside-click listener (a mousedown outside both the cell and any
// `.ant-picker-dropdown`) plus Escape, never on blur.
const PICKER_EDITABLE_ELEMENTS = new Set([
    "form-dropdown",
    "form-optional-dropdown",
    "form-date",
    "form-optional-date",
    "form-time",
    "form-optional-time",
    "form-time-with-utcoffset",
    "form-optional-time-with-utcoffset",
    "form-datetime",
    "form-optional-datetime",
    "form-datetime-with-utcoffset",
    "form-optional-datetime-with-utcoffset",
]);

// Custom-object attribute element types edited via CLICK-TO-EDIT: the read-only
// value is shown until the cell is clicked, then a single-line editor appears
// and commits on blur / Enter (revert on Escape or on a failed save). This is
// the second slice of inline list-editing (operator-task-fe-inline-edit-list-
// views): plain-input scalar types (single-line string, integer). Date / time /
// datetime are click-to-reveal pickers instead (the PICKER set above — a portaled
// calendar panel makes commit-on-blur race the pick, so they commit on the
// picker's discrete onChange); text / markdown / json get a popover overlay
// editor.
const CLICK_TO_EDIT_EDITABLE_ELEMENTS = new Set([
    "form-string",
    "form-optional-string",
    "form-integer",
    "form-optional-integer",
]);

// Custom-object attribute element types edited via a POPOVER OVERLAY: large /
// multi-line content (text -> Monaco, json -> YAML editor) does not fit an
// inline row cell, so an edit affordance opens an anchored popover hosting the
// SAME datatype-aware editor as the detail form, with explicit Save / Cancel
// (save-on-blur is wrong here — a Monaco editor blurs on its own autocomplete
// popups, toolbar clicks, etc.). This is the third slice of inline list-editing
// (operator-task-fe-inline-edit-list-views). Backend CO datatypes TEXT/JSON map
// to these elements (resources/object_template.py
// get_object_template_attribute_schema).
//
// MARKDOWN is intentionally NOT here. In list/table cells a markdown value is
// VIEW-ONLY: the cell renders a height-clipped preview with an EXPAND affordance
// that opens the full rendered markdown in a read-only popover (see
// MarkdownExpandCell / AttributeValueSchemaDTO). In-table markdown EDITING is a
// separate, deferred feature (operator-task-fe-markdown-in-tables-inline-edit),
// so markdown attributes get editMode null and render through the read-only DTO.
const POPOVER_EDITABLE_ELEMENTS = new Set([
    "form-text",
    "form-optional-text",
    "form-json",
    "form-optional-json",
]);

/**
 * Resolve how a custom-object attribute cell may be edited inline, or null when
 * it is read-only. Gated on the row being writable (not read-only, not in trash,
 * has an id) and the attribute not being secret:
 *  - "immediate": live control (boolean switch) that commits on a single
 *    discrete interaction.
 *  - "picker": click-to-reveal control (enum dropdown shown as a badge at rest,
 *    or a date/time/datetime picker) committing on its discrete onChange
 *    (portal-aware, never on blur).
 *  - "click": click-to-edit scalar input (string / integer) committing on blur.
 *  - "popover": an edit affordance opens an anchored overlay editor (text /
 *    json) with explicit Save / Cancel. (Markdown is view-only in list cells —
 *    see MarkdownExpandCell — so it returns null here.)
 */
export const getAttributeEditMode = (
    record: any,
    schema: any
): "immediate" | "picker" | "click" | "popover" | null => {
    const element: string = schema?.element ?? "";
    const isWritableRow =
        !!record?.id &&
        !record?.is_readonly &&
        !record?.is_project_readonly &&
        !record?.is_bundle_readonly &&
        !record?.deleted_at;
    if (!isWritableRow || schema?.secret) {
        return null;
    }
    if (IMMEDIATE_EDITABLE_ELEMENTS.has(element)) {
        return "immediate";
    }
    if (PICKER_EDITABLE_ELEMENTS.has(element)) {
        return "picker";
    }
    if (CLICK_TO_EDIT_EDITABLE_ELEMENTS.has(element)) {
        return "click";
    }
    if (POPOVER_EDITABLE_ELEMENTS.has(element)) {
        return "popover";
    }
    return null;
};

/**
 * Whether a custom-object attribute cell should render an inline editor rather
 * than a read-only value (either edit mode).
 */
export const isAttributeCellEditable = (record: any, schema: any) =>
    getAttributeEditMode(record, schema) !== null;

/**
 * Dispatch a single-attribute update on a custom object via the existing
 * single-record update saga. The GraphQL update_custom_object mutation sets the
 * whole `value` column, so send the FULL value blob with just this one attribute
 * changed (a partial blob would drop the other attributes). Returns a promise
 * that resolves on success and rejects on failure (the saga surfaces the error
 * via its own toast).
 */
const dispatchAttributeUpdate = (
    dispatch: any,
    record: any,
    property: string,
    newValue: any
) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "custom_object",
            variables: {
                id: record?.id,
                value: { ...(record?.value ?? {}), [property]: newValue },
                // Optimistic-concurrency guard: reject (→ reload/overwrite dialog)
                // instead of silently clobbering a concurrent change.
                expected_version: record?.record_version,
            },
        });
    });

export const getSchemaFormColumns = (
    currentSchema,
    {
        sortable = false,
        editable = false,
        includeKeys = null,
    }: {
        sortable?: boolean;
        editable?: boolean;
        // When a (non-null) array is given, build columns ONLY for these attribute
        // keys (in schema-property order), skipping every other attribute. Callers
        // that project the fetched `value` to a subset of keys (the dashboard
        // record_list widget's `value_keys` projection) pass the SAME key set here,
        // so the built attribute columns and the fetched value keys stay in lock-
        // step — there is then no attribute column whose value was not fetched, so
        // toggling a column visible can never render an empty cell. Omit / null →
        // build columns for every attribute (unchanged behaviour).
        includeKeys?: string[] | null;
    } = {}
) => {
    const schemaProperties = currentSchema.properties;
    const isShownColumn = (property) => !(currentSchema.hidden || [])?.includes(property);
    const includeSet = Array.isArray(includeKeys) ? new Set(includeKeys) : null;

    const shownColumns: TableColumn[] = [];
    const hiddenColumns: TableColumn[] = [];
    for (const property in schemaProperties) {
        if (includeSet && !includeSet.has(property)) {
            continue;
        }
        const getColumn = (isShown: boolean): TableColumn => ({
            label: schemaProperties[property].label,
            // When the list supports server-side sorting on attribute values, the
            // column identity (MUI `name`) must be the `value.<attr>` order token
            // the backend understands (custom_object_list order arg accepts
            // "+value.<attr>"/"-value.<attr>"). Keying it as `value.<attr>` keeps
            // the sort emit (onColumnSortChange -> "±value.<attr>"), the sort
            // restore (initialQuery.order round-trip), and column persistence all
            // consistent. In the non-sortable default (e.g. dashboard record_list
            // widget) keep the bare attribute name so configured column keys and
            // persisted settings are unchanged. The `dto` reads the value via the
            // `property` closure, so the key change does not affect rendering.
            key: sortable ? `value.${property}` : property,
            shown: isShown,
            sortable,
            cellStyle: getAttributeColumnCellStyle(schemaProperties[property]),
            // `row` is the whole record (RecordTable calls dto(rowData, refresh));
            // the attribute value lives at row.value[property]. `refresh` reloads
            // the list after a successful inline edit so later edits read fresh
            // row data.
            dto: (row, refresh) => {
                const editMode = editable
                    ? getAttributeEditMode(row, schemaProperties[property])
                    : null;
                if (editMode === "immediate") {
                    return (
                        <EditableAttributeValueSchemaDTO
                            record={row}
                            property={property}
                            value={row?.value?.[property]}
                            schema={schemaProperties[property]}
                            refresh={refresh}
                        />
                    );
                }
                if (editMode === "picker") {
                    return (
                        <ClickToEditPickerAttributeValueSchemaDTO
                            record={row}
                            property={property}
                            value={row?.value?.[property]}
                            schema={schemaProperties[property]}
                            refresh={refresh}
                        />
                    );
                }
                if (editMode === "click") {
                    return (
                        <ClickToEditAttributeValueSchemaDTO
                            record={row}
                            property={property}
                            value={row?.value?.[property]}
                            schema={schemaProperties[property]}
                            refresh={refresh}
                        />
                    );
                }
                if (editMode === "popover") {
                    return (
                        <PopoverEditAttributeValueSchemaDTO
                            record={row}
                            property={property}
                            value={row?.value?.[property]}
                            schema={schemaProperties[property]}
                            refresh={refresh}
                        />
                    );
                }
                return (
                    <AttributeValueSchemaDTO
                        value={row?.value?.[property]}
                        schema={schemaProperties[property]}
                    />
                );
            },
        });
        if (isShownColumn(property) && shownColumns.length < 7) {
            shownColumns.push(getColumn(true));
        } else {
            hiddenColumns.push(getColumn(false));
        }
    }
    return [...shownColumns, ...hiddenColumns];
};

/**
 * View-only markdown attribute cell for list / table views
 * (operator-task-fe-markdown-in-tables-display-rework). A markdown value rendered
 * inline blows out the row height, and full rich markdown (headers, code blocks,
 * tables) reads badly when hard-clipped. So the cell shows a height-clipped
 * preview plus a hover-revealed EXPAND affordance (an expand icon, not an edit
 * pen) that opens the full rendered markdown in a read-only popover — the
 * affordance matches what it does (expand / open the content), per
 * ui-design-principle-affordance-interactivity.
 *
 * Markdown is intentionally NOT editable inline here (no edit pen, no nested
 * editor): in-table markdown editing is deferred to
 * operator-task-fe-markdown-in-tables-inline-edit. Edit markdown by opening the
 * record. The popover renders the same read-only SchemaFormFragment markdown as
 * the cell preview, unclipped and vertically scrollable.
 */
export const MarkdownExpandCell = ({ value, schema, ajv }: any) => {
    const renderMarkdown = () => (
        <SchemaFormFragment
            isRoot={true}
            isReadOnly={true}
            schema={schema}
            schemaPath={[]}
            formData={value}
            dataPath={[]}
            setData={() => {}}
            showLabel
            ajv={ajv}
            isRecordDisabled={true}
        />
    );
    // Only offer the expand affordance when there is content to expand — an empty
    // markdown attribute just shows its (empty) preview.
    const hasContent =
        typeof value === "string" ? value.trim().length > 0 : value !== null && value !== undefined;
    return (
        <div className="inlineEditPopoverCell markdownExpandCell">
            <div className="attributeValueSchemaContainer attributeValueSchemaContainer--clamp inlineEditPopoverCell__preview">
                {renderMarkdown()}
            </div>
            {hasContent && (
                <Popover
                    trigger={
                        <span
                            className="markdownExpandCell__expandTrigger"
                            aria-label="Expand"
                            title="Expand"
                        >
                            <Icon
                                byName="arrowExpand"
                                className="inlineEditPopoverCell__pencil markdownExpandCell__expandIcon"
                            />
                        </span>
                    }
                    toggleClassName="inlineEditPopoverCell__trigger"
                    closeOnInsideClick={false}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    contentClassName="markdownExpandPopover"
                >
                    <div className="markdownExpandPopover__body attributeValueSchemaContainer">
                        {renderMarkdown()}
                    </div>
                </Popover>
            )}
        </div>
    );
};

export const AttributeValueSchemaDTO = ({ value, schema, ajv }: any) => {
    // Read-only attribute cells (object_template CO list, dashboard record_list
    // widget) render a SchemaFormFragment. A dropdown/enum attribute
    // (form-(optional-)dropdown, e.g. an ALLOWED_VALUES attribute) resolves its
    // selected option by validating the stored value against each oneOf/anyOf
    // branch with `ajv` (SchemaFormDropdown). Without an ajv instance that lookup
    // throws → no branch matches → selectedIndex -1 → the cell renders the literal
    // "Invalid choice". getSchemaFormColumns never threads an ajv through, so fall
    // back to the shared RouterContext ajv (the same instance the interactive form
    // uses) whenever one is not explicitly provided.
    const contextAjv = useContext(RouterContext)?.ajv;
    // Markdown attribute values are VIEW-ONLY in list/table cells: a height-
    // clipped preview plus a hover-revealed EXPAND affordance opening the full
    // rendered markdown in a read-only popover (MarkdownExpandCell). The affordance
    // matches the behaviour (expand/open, not edit) per
    // ui-design-principle-affordance-interactivity.
    const isMarkdown =
        schema?.element === "form-markdown" || schema?.element === "form-optional-markdown";
    if (isMarkdown) {
        return <MarkdownExpandCell value={value} schema={schema} ajv={ajv ?? contextAjv} />;
    }
    const fragment = (
        <SchemaFormFragment
            isRoot={true}
            isReadOnly={true}
            schema={schema}
            schemaPath={[]}
            formData={value}
            dataPath={[]}
            setData={() => {}}
            showLabel
            ajv={ajv ?? contextAjv}
            isRecordDisabled={true}
            additionalCodeEditorRootProps={{
                initialHeight: 100,
                minHeight: 100,
            }}
        />
    );
    // Cap the rendered height of a read-only attribute value so one long / multi-
    // line value (e.g. a multi-line text attribute) cannot balloon the whole table
    // row — a single tall cell otherwise forces every other cell in its row to the
    // same height, pushing row counts down (Margot,
    // operator-task-fe-recordtable-row-height-stability-fixed-rows). This read-only
    // DTO is also what the inline-edit cells render at rest (click-to-edit read
    // state, popover preview), so clamping it bounds those previews too; the active
    // editors render their own fragment (not this DTO) and are unaffected.
    return (
        <div className="attributeValueSchemaContainer attributeValueSchemaContainer--clamp">
            {fragment}
        </div>
    );
};

/**
 * Editable custom-object attribute cell (inline list-editing, scope (i):
 * operator-task-fe-inline-edit-list-views). Renders the same datatype-aware
 * SchemaFormFragment as the read-only cell but interactive (isReadOnly=false),
 * keeping isRecordDisabled=true so the per-field action buttons (unset / reset /
 * clear) stay hidden in the compact cell. Used only for the immediately-editable
 * element types (boolean switch, enum dropdown) that commit on a single
 * interaction — no click-to-edit/focus-blur handling needed (boolean switch,
 * enum dropdown, and the date/time/datetime pickers, which fire a single
 * discrete onChange on a confirmed pick).
 *
 * On commit it dispatches the existing single-record update saga
 * (START_UPDATE_RESOURCE) with the FULL value blob and this one attribute
 * changed — the GraphQL update_custom_object mutation sets the whole `value`
 * column, so a partial blob would drop the other attributes. The cell shows the
 * new value optimistically and reverts if the update fails (the saga surfaces
 * the error via its own toast). On success it refreshes the list so subsequent
 * edits read fresh row data.
 */
export const EditableAttributeValueSchemaDTO = ({
    record,
    property,
    value,
    schema,
    refresh,
}: any) => {
    const dispatch = useDispatch();
    const contextAjv = useContext(RouterContext)?.ajv;
    const [localValue, setLocalValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);

    // Re-sync the optimistic cell value when the underlying row value changes
    // externally (list refresh / poll) while no save is in flight.
    useEffect(() => {
        if (!isSaving) {
            setLocalValue(value);
        }
    }, [value, isSaving]);

    const commit = useCallback(
        (newValue: any) => {
            if (newValue === value) {
                return;
            }
            setLocalValue(newValue);
            setIsSaving(true);
            dispatchAttributeUpdate(dispatch, record, property, newValue)
                .then(() => {
                    setIsSaving(false);
                    refresh?.();
                })
                .catch(() => {
                    // Revert the optimistic value; the update saga already
                    // surfaces the failure via a toast notification.
                    setLocalValue(value);
                    setIsSaving(false);
                });
        },
        [dispatch, record, property, value, refresh]
    );

    return (
        <div className="attributeValueSchemaContainer">
            <SchemaFormFragment
                isRoot={true}
                isReadOnly={false}
                schema={schema}
                schemaPath={[]}
                formData={localValue}
                dataPath={[]}
                setData={commit}
                showLabel
                ajv={contextAjv}
                isRecordDisabled={true}
                additionalCodeEditorRootProps={{
                    initialHeight: 100,
                    minHeight: 100,
                }}
            />
        </div>
    );
};

/**
 * Click-to-reveal picker custom-object attribute cell (inline list-editing scope
 * (i): operator-task-fe-inline-edit-list-views-followups, item 2). Used for the
 * date / time / datetime element types. The read-only value shows until the cell
 * is clicked, then the SAME datatype-aware SchemaFormFragment picker (antd
 * DatePicker/TimePicker, interactive) is revealed and commits on the picker's
 * single discrete `onChange` (a confirmed pick) — the same commit-on-interaction
 * shape as the boolean/dropdown immediate cell, but gated behind a click so a
 * dense list shows read-only values rather than a permanently-open input in
 * every row.
 *
 * Exit / cancel model differs from the string/integer click-to-edit cell on
 * purpose: the picker's calendar/time panel is portaled OUTSIDE the cell (antd
 * renders it in a document-level `.ant-picker-dropdown`), so a click into the
 * panel blurs the cell — a commit-on-blur model would tear the editor down
 * before the pick lands. This cell instead:
 *  - commits on the picker's onChange, then returns to read-only;
 *  - cancels (returns to read-only, no save) on Escape, or on a mousedown that
 *    lands outside BOTH the cell and any portaled `.ant-picker-dropdown`.
 * A `finished` latch makes each edit session commit-or-cancel exactly once.
 * Optimistic value shows immediately; a failed save reverts (the saga toasts the
 * error); a successful save refreshes the list so later edits read fresh data.
 */
export const ClickToEditPickerAttributeValueSchemaDTO = ({
    record,
    property,
    value,
    schema,
    refresh,
}: any) => {
    const dispatch = useDispatch();
    const contextAjv = useContext(RouterContext)?.ajv;
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Latches once an edit session ends (commit or cancel) so a trailing
    // onChange / outside-click cannot commit or cancel a second time.
    const finishedRef = useRef(false);

    // Re-sync the display value when the underlying row value changes externally
    // (list refresh / poll) while the cell is idle (not editing, not saving).
    useEffect(() => {
        if (!isEditing && !isSaving) {
            setLocalValue(value);
        }
    }, [value, isEditing, isSaving]);

    // Focus the picker input when entering edit mode so the user can open the
    // panel straight away (antd opens its dropdown on focus/click).
    useEffect(() => {
        if (isEditing && containerRef.current) {
            const el = containerRef.current.querySelector<HTMLInputElement>("input");
            el?.focus();
        }
    }, [isEditing]);

    const beginEdit = useCallback(() => {
        if (isSaving) {
            return;
        }
        finishedRef.current = false;
        setLocalValue(value);
        setIsEditing(true);
    }, [isSaving, value]);

    const commit = useCallback(
        (newValue: any) => {
            if (finishedRef.current) {
                return;
            }
            finishedRef.current = true;
            setIsEditing(false);
            if (newValue === value) {
                return;
            }
            setLocalValue(newValue);
            setIsSaving(true);
            dispatchAttributeUpdate(dispatch, record, property, newValue)
                .then(() => {
                    setIsSaving(false);
                    refresh?.();
                })
                .catch(() => {
                    setLocalValue(value);
                    setIsSaving(false);
                });
        },
        [dispatch, record, property, value, refresh]
    );

    const cancel = useCallback(() => {
        if (finishedRef.current) {
            return;
        }
        finishedRef.current = true;
        setLocalValue(value);
        setIsEditing(false);
    }, [value]);

    // Portal-aware outside-click: while editing, a mousedown that lands outside
    // both the cell and any portaled antd picker dropdown exits edit mode (revert,
    // no save). Clicks inside the `.ant-picker-dropdown` panel (selecting a date /
    // time) are ignored here — the picker's own onChange commits those.
    useEffect(() => {
        if (!isEditing) {
            return;
        }
        const onDocMouseDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (containerRef.current?.contains(target as Node)) {
                return;
            }
            if (target?.closest?.(".ant-picker-dropdown")) {
                return;
            }
            cancel();
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [isEditing, cancel]);

    if (!isEditing) {
        return (
            <div
                className="inlineEditableCell"
                role="button"
                tabIndex={0}
                title="Click to edit"
                onClick={beginEdit}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        beginEdit();
                    }
                }}
            >
                <AttributeValueSchemaDTO value={localValue} schema={schema} />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="attributeValueSchemaContainer isInlineEditing"
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    event.preventDefault();
                    cancel();
                }
            }}
        >
            <SchemaFormFragment
                isRoot={true}
                isReadOnly={false}
                isRecordDisabled={false}
                hideActionButtons={true}
                schema={schema}
                schemaPath={[]}
                formData={localValue}
                dataPath={[]}
                setData={commit}
                showLabel
                ajv={contextAjv}
                additionalCodeEditorRootProps={{
                    initialHeight: 100,
                    minHeight: 100,
                }}
            />
        </div>
    );
};

/**
 * Click-to-edit custom-object attribute cell (inline list-editing scope (i),
 * slice (b): operator-task-fe-inline-edit-list-views). Renders the read-only
 * value until the cell is clicked, then swaps in the SAME datatype-aware
 * SchemaFormFragment editor (interactive, isRecordDisabled=false so a
 * single-line string cell renders its input rather than plain text;
 * hideActionButtons keeps the compact cell clutter-free). Used for the
 * click-to-edit scalar types (single-line string, integer) that need an
 * explicit edit + commit-on-blur cycle.
 *
 * Commit model: local edits stay in `localValue`; the change is saved (via the
 * shared single-attribute update) on blur out of the cell or on Enter, and
 * reverted on Escape. A `finished` latch makes the edit session commit exactly
 * once (an Enter that unmounts the input then fires a trailing blur must not
 * double-save). Optimistic value shows immediately; a failed save reverts (the
 * saga toasts the error); a successful save refreshes the list so later edits
 * read fresh row data.
 */
export const ClickToEditAttributeValueSchemaDTO = ({
    record,
    property,
    value,
    schema,
    refresh,
}: any) => {
    const dispatch = useDispatch();
    const contextAjv = useContext(RouterContext)?.ajv;
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Latches once an edit session ends (commit or cancel) so a trailing blur
    // after Enter does not commit a second time.
    const finishedRef = useRef(false);

    // Re-sync the display value when the underlying row value changes externally
    // (list refresh / poll) while the cell is idle (not editing, not saving).
    useEffect(() => {
        if (!isEditing && !isSaving) {
            setLocalValue(value);
        }
    }, [value, isEditing, isSaving]);

    // Focus (and select) the input when entering edit mode.
    useEffect(() => {
        if (isEditing && containerRef.current) {
            const el = containerRef.current.querySelector<HTMLInputElement | HTMLTextAreaElement>(
                "input, textarea"
            );
            if (el) {
                el.focus();
                if (typeof el.select === "function") {
                    el.select();
                }
            }
        }
    }, [isEditing]);

    const beginEdit = useCallback(() => {
        if (isSaving) {
            return;
        }
        finishedRef.current = false;
        setLocalValue(value);
        setIsEditing(true);
    }, [isSaving, value]);

    const commit = useCallback(
        (newValue: any) => {
            if (finishedRef.current) {
                return;
            }
            finishedRef.current = true;
            setIsEditing(false);
            if (newValue === value) {
                return;
            }
            setLocalValue(newValue);
            setIsSaving(true);
            dispatchAttributeUpdate(dispatch, record, property, newValue)
                .then(() => {
                    setIsSaving(false);
                    refresh?.();
                })
                .catch(() => {
                    setLocalValue(value);
                    setIsSaving(false);
                });
        },
        [dispatch, record, property, value, refresh]
    );

    const cancel = useCallback(() => {
        if (finishedRef.current) {
            return;
        }
        finishedRef.current = true;
        setLocalValue(value);
        setIsEditing(false);
    }, [value]);

    if (!isEditing) {
        return (
            <div
                className="inlineEditableCell"
                role="button"
                tabIndex={0}
                title="Click to edit"
                onClick={beginEdit}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        beginEdit();
                    }
                }}
            >
                <AttributeValueSchemaDTO value={localValue} schema={schema} />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="attributeValueSchemaContainer isInlineEditing"
            onBlur={(event) => {
                // Commit only when focus leaves the cell entirely (not when it
                // moves between elements inside the editor).
                if (!containerRef.current?.contains(event.relatedTarget as Node)) {
                    commit(localValue);
                }
            }}
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    event.preventDefault();
                    cancel();
                } else if (event.key === "Enter") {
                    event.preventDefault();
                    commit(localValue);
                }
            }}
        >
            <SchemaFormFragment
                isRoot={true}
                isReadOnly={false}
                isRecordDisabled={false}
                hideActionButtons={true}
                schema={schema}
                schemaPath={[]}
                formData={localValue}
                dataPath={[]}
                setData={setLocalValue}
                showLabel
                ajv={contextAjv}
                additionalCodeEditorRootProps={{
                    initialHeight: 100,
                    minHeight: 100,
                }}
            />
        </div>
    );
};

/**
 * Popover-overlay custom-object attribute cell (inline list-editing scope (i),
 * slice (c): operator-task-fe-inline-edit-list-views). Used for large /
 * multi-line attribute types (text -> Monaco, json -> YAML, markdown) that do
 * not fit an inline row cell. The cell shows the read-only value plus a pencil
 * edit affordance; clicking the pencil opens an anchored Popover hosting the
 * SAME datatype-aware SchemaFormFragment editor as the detail form, with
 * explicit Save / Cancel.
 *
 * Save model differs from the boolean/dropdown (immediate) and string/integer
 * (commit-on-blur) cells on purpose: a Monaco/markdown editor blurs on its own
 * autocomplete popups, toolbar clicks, resize handles, etc., so save-on-blur
 * would commit unpredictably. Instead editing stays local until the user clicks
 * Save (or Ctrl/Cmd+Enter); Cancel / Escape / outside-click discards.
 *
 * The latest edited value is held in a ref as well as state: form-json only
 * flushes its parsed value to setData on the editor's blur, which fires when the
 * Save button takes focus (mousedown) — synchronously before the button's click
 * handler runs — so reading the ref at Save time captures it without racing the
 * asynchronous state update. Commit reuses the shared single-attribute update
 * (full value blob, one attribute changed); optimistic value, revert on failure
 * (the saga toasts the error), refresh the list on success.
 */
export const PopoverEditAttributeValueSchemaDTO = ({
    record,
    property,
    value,
    schema,
    refresh,
}: any) => {
    const dispatch = useDispatch();
    const contextAjv = useContext(RouterContext)?.ajv;
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    // Mirrors the local edited value so the Save handler reads the freshest
    // value even when the editor flushes it via an onBlur that fires in the same
    // click as Save (form-json) — a ref update is synchronous, the state update
    // is not.
    const latestValueRef = useRef(value);
    // Set on the Save button's mousedown (which fires before the editor blurs).
    // The markdown @monaco-editor/react editor blurs on the Save click and makes
    // MUIPopover fire onClose -> cancel(); this flag lets cancel() ignore that one
    // spurious close so it does not reset latestValueRef to the original value
    // before save() reads it. The form-string/text/json editors do not trigger
    // that onClose on a Save click, so their save path is unaffected.
    const savingInitiatedRef = useRef(false);

    // Re-sync the display value when the underlying row value changes externally
    // (list refresh / poll) while the editor is closed and no save is in flight.
    useEffect(() => {
        if (!isOpen && !isSaving) {
            setLocalValue(value);
            latestValueRef.current = value;
        }
    }, [value, isOpen, isSaving]);

    const openEditor = useCallback(() => {
        if (isSaving) {
            return;
        }
        setLocalValue(value);
        latestValueRef.current = value;
        savingInitiatedRef.current = false;
        setIsOpen(true);
    }, [isSaving, value]);

    // The editor's setData: keep the ref and state in sync as the user edits.
    const handleChange = useCallback((newValue: any) => {
        latestValueRef.current = newValue;
        setLocalValue(newValue);
    }, []);

    const cancel = useCallback(() => {
        if (savingInitiatedRef.current) {
            // A Save click is in flight (its mousedown set this flag). The
            // markdown editor blurs on that click and triggers MUIPopover's
            // onClose -> this handler; swallow that single spurious close so it
            // does not discard the edit before save() commits it.
            savingInitiatedRef.current = false;
            return;
        }
        setLocalValue(value);
        latestValueRef.current = value;
        setIsOpen(false);
    }, [value]);

    const save = useCallback(() => {
        savingInitiatedRef.current = false;
        const newValue = latestValueRef.current;
        setIsOpen(false);
        if (newValue === value) {
            return;
        }
        setLocalValue(newValue);
        setIsSaving(true);
        dispatchAttributeUpdate(dispatch, record, property, newValue)
            .then(() => {
                setIsSaving(false);
                refresh?.();
            })
            .catch(() => {
                setLocalValue(value);
                latestValueRef.current = value;
                setIsSaving(false);
            });
    }, [dispatch, record, property, value, refresh]);

    return (
        <div className="inlineEditPopoverCell">
            <div className="attributeValueSchemaContainer inlineEditPopoverCell__preview">
                <AttributeValueSchemaDTO value={localValue} schema={schema} />
            </div>
            <Popover
                trigger={
                    <Icon byName="pencilOutline" className="inlineEditPopoverCell__pencil" />
                }
                toggleClassName="inlineEditPopoverCell__trigger"
                isOpen={isOpen}
                actionOnOpen={openEditor}
                closeOnInsideClick={false}
                // Do NOT use the Popover's custom document-level useOutsideClick
                // listener here. It fires closeHandler (cancel -> setIsOpen(false))
                // on stray document clicks that do not hit the MUI backdrop, which
                // closed this controlled popover within ~0.5s of opening — before
                // the lazily-loaded Monaco editor even mounted, making text/json/
                // markdown inline editing unusable. Outside-click and Escape still
                // close the popover via MUIPopover's own onClose (backdrop + Escape)
                // -> onBlur={cancel}, the robust standard path.
                closeOnOutsideClick={false}
                closeHandler={cancel}
                onBlur={cancel}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                contentClassName="inlineEditPopover"
            >
                <div
                    className="inlineEditPopover__body attributeValueSchemaContainer isInlineEditing"
                    onKeyDown={(event) => {
                        if (event.key === "Escape") {
                            event.preventDefault();
                            cancel();
                        } else if (
                            event.key === "Enter" &&
                            (event.ctrlKey || event.metaKey)
                        ) {
                            event.preventDefault();
                            save();
                        }
                    }}
                >
                    <SchemaFormFragment
                        isRoot={true}
                        isReadOnly={false}
                        isRecordDisabled={false}
                        hideActionButtons={true}
                        schema={schema}
                        schemaPath={[]}
                        formData={localValue}
                        dataPath={[]}
                        setData={handleChange}
                        showLabel
                        ajv={contextAjv}
                        additionalCodeEditorRootProps={{
                            initialHeight: 200,
                            minHeight: 160,
                        }}
                    />
                </div>
                <div className="inlineEditPopover__footer">
                    <Button
                        variation="secondary"
                        size="small"
                        label="Cancel"
                        onClick={cancel}
                    />
                    <span
                        // Layout-neutral wrapper (Button does not forward
                        // onMouseDown). mousedown fires before the editor blur,
                        // so this marks the save-in-flight before the markdown
                        // editor's blur can trigger MUIPopover onClose -> cancel().
                        style={{ display: "contents" }}
                        onMouseDown={() => {
                            savingInitiatedRef.current = true;
                        }}
                    >
                        <Button
                            variation="primary"
                            size="small"
                            label="Save"
                            loading={isSaving}
                            onClick={save}
                        />
                    </span>
                </div>
            </Popover>
        </div>
    );
};
